// src/components/Registros/forms/TeletrabajoForm.jsx
import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaLaptop } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SelectMult from '../../SettingNot/SelectMult';
import { useNavigate } from 'react-router-dom';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos } from '../../../api/common';

const TeletrabajoForm = ({ onClose, onSubmit, tipoRegistro, solicitante}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    solicitante: solicitante,
    titulo: '',
    fechaInicio: '',
    fechaFin: '',
    tipoTeletrabajo: 'tiempo_completo',
    direccionTrabajo: '',
    justificacion: '',
    descripcion: '',
    responsablesAprobacion: [],
    adjuntos: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const responsables = [
    { id: '1', nombre: 'Juan Pérez', cargo: 'Jefe Inmediato' },
    { id: '2', nombre: 'María García', cargo: 'Jefe de RRHH' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const calcularDias = () => {
    if (formData.fechaInicio && formData.fechaFin) {
      const inicio = new Date(formData.fechaInicio);
      const fin = new Date(formData.fechaFin);
      const diffTime = Math.abs(fin - inicio);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target; // value es un array de IDs: ['id1', 'id2', ...]
    
    // Transformar el array de IDs a objetos con la estructura del modelo
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    }));
    setFormData(prev => ({
      ...prev,
      responsablesAprobacion: responsablesFormateados
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera (ver handleSubmit).

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es requerida';
    }

    if (formData.fechaInicio && formData.fechaFin) {
      if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
        newErrors.fechaFin = 'La fecha fin debe ser mayor o igual a la fecha inicio';
      }
    }

    if (!formData.direccionTrabajo.trim()) {
      newErrors.direccionTrabajo = 'La dirección de trabajo es requerida';
    }

    if (!formData.justificacion.trim()) {
      newErrors.justificacion = 'La justificación es requerida';
    }

    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setLoading(true);

    try {
      const dataToSubmit = {
        ...formData,
        titulo: 'Teletrabajo',
        tipoSolicitud: 'teletrabajo',
        causaInactividad: false
      };

      await crearDatos('Registro', dataToSubmit);

      await Swal.fire({
        icon: 'success',
        title: 'Solicitud Enviada',
        text: 'Tu solicitud de teletrabajo ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      navigate('/Solicitudes');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) {
        onClose();
      }
    });
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #20c997 0%, #198754 100%)' }}>
          <div className="d-flex align-items-center">
            <Button variant="light" size="sm" onClick={handleCancel} className="me-3">
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">Solicitud de Teletrabajo</h3>
              <p className="mb-0 opacity-75">Complete el formulario para solicitar trabajo remoto</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Alert variant="info" className="d-flex align-items-center">
            <FaLaptop className="me-2" size={20} />
            <div>
              <strong>Nota:</strong> Asegúrate de contar con las condiciones técnicas necesarias 
              (internet estable, espacio de trabajo adecuado) para trabajar de forma remota.
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold" style={{display:'none'}}>Título (auto)</Form.Label>
                    <Form.Control
                      type="hidden"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      placeholder="Ej: Solicitud de teletrabajo temporal"
                      isInvalid={!!errors.titulo}
                      maxLength={100}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.titulo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Tipo de Teletrabajo *</Form.Label>
                    <Form.Select
                      name="tipoTeletrabajo"
                      value={formData.tipoTeletrabajo}
                      onChange={handleChange}
                    >
                      <option value="tiempo_completo">Tiempo Completo</option>
                      <option value="parcial">Parcial (algunos días)</option>
                      <option value="ocasional">Ocasional</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {formData.tipoTeletrabajo === 'tiempo_completo' && 'Trabajarás de forma remota todos los días'}
                      {formData.tipoTeletrabajo === 'parcial' && 'Combinarás días presenciales y remotos'}
                      {formData.tipoTeletrabajo === 'ocasional' && 'Solo algunos días específicos'}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Periodo */}
            <div className="form-section">
              <h5 className="form-section-title">Periodo de Teletrabajo</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Inicio *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaInicio}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaInicio}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Fin *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {calcularDias() > 0 && (
                <Alert variant="success">
                  Duración del teletrabajo: <strong>{calcularDias()}</strong> día(s)
                </Alert>
              )}
            </div>

            {/* Ubicación */}
            <div className="form-section">
              <h5 className="form-section-title">Ubicación de Trabajo Remoto</h5>
              
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Dirección de Trabajo *</Form.Label>
                    <Form.Control
                      type="text"
                      name="direccionTrabajo"
                      value={formData.direccionTrabajo}
                      onChange={handleChange}
                      placeholder="Dirección completa desde donde trabajarás"
                      isInvalid={!!errors.direccionTrabajo}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.direccionTrabajo}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Especifica la ciudad y dirección desde donde trabajarás
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Justificación */}
            <div className="form-section">
              <h5 className="form-section-title">Justificación</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Motivo de la Solicitud *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="justificacion"
                  value={formData.justificacion}
                  onChange={handleChange}
                  placeholder="Explica el motivo por el cual solicitas el teletrabajo..."
                  isInvalid={!!errors.justificacion}
                  maxLength={500}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.justificacion}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {formData.justificacion.length}/500 caracteres
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Observaciones Adicionales</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Información sobre recursos técnicos disponibles, horarios, etc..."
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {formData.descripcion.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Responsables */}
            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                //dataDefault={itemData?.userAutorizados}
                handledata={handleResponsableChange}
                name='responsablesAprobacion'
                collection={"Usuarios"}
                field={"nombre"}
                baseFilter={[ // Body para la consuta aggregate de mongo para metrologos y que esten activos.
                        {
                            "$match": {
                            'status': 'activo'
                            } 
                        },
                        {
                            "$lookup": {
                            "from": "roles",
                            "localField": "role",
                            "foreignField": "_id",
                            "as": "result"
                            }
                        },
                        {
                            "$match": {
                            "result.nombre": { $ne: "Cliente" }
                            }
                        }
                        ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">
                    {errors.responsablesAprobacion}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="teletrabajo"
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                disabled={loading}
                className="px-4"
              >
                <FaTimes className="me-2" />
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default TeletrabajoForm;